import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { company } from "@/config/content";
import { useLang } from "@/i18n/LanguageContext";
import { Seo } from "@/components/Seo";
import { Overline } from "@/components/Reveal";

function LegalShell({ title, crumb, path, description, children }) {
  const { t } = useLang();
  return (
    <>
      <Seo title={`${title} | ${company.name}`} description={description} path={path} />
      <section className="bg-carbon pb-20 pt-32">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-xs text-steel">
            <Link to="/" className="hover:text-amber">{t.breadcrumbHome}</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-ice">{crumb}</span>
          </nav>
          <Overline>{crumb}</Overline>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-ice sm:text-4xl">{title}</h1>
          <div className="prose-legal mt-8 space-y-6 text-sm leading-relaxed text-steel">{children}</div>
          <p className="mt-10 rounded-md border border-dashed border-edge bg-asphalt/50 p-4 text-xs text-steel">
            ⚠ Placeholder — Testo legale da rivedere/completare con dati reali (titolare, P.IVA: {company.vat}, email: {company.email}).
          </p>
        </div>
      </section>
    </>
  );
}

function H2({ children }) {
  return <h2 className="font-display text-lg font-bold text-ice">{children}</h2>;
}

export function PrivacyPolicy() {
  const { lang } = useLang();
  if (lang === "en") {
    return (
      <LegalShell title="Privacy Policy" crumb="Privacy Policy" path="/privacy" description="Privacy policy of EMME SERVICE CAR.">
        <p>This policy explains how {company.name} processes personal data of visitors and customers, in accordance with the GDPR (EU 2016/679).</p>
        <H2>Data controller</H2>
        <p>{company.name} — {company.address} — VAT {company.vat} — {company.email}.</p>
        <H2>Data we collect</H2>
        <p>Contact data you voluntarily provide (name, phone, email), transport request details and, only with your consent, the geographic location you share to receive assistance.</p>
        <H2>Purpose and legal basis</H2>
        <p>Data is used solely to respond to your request and organise the service. Location data is never stored without your consent.</p>
        <H2>Retention</H2>
        <p>We keep data only for the time needed to handle your request and any related obligations.</p>
        <H2>Your rights</H2>
        <p>You may request access, rectification, deletion or restriction of your data by writing to {company.email}.</p>
      </LegalShell>
    );
  }
  return (
    <LegalShell title="Privacy Policy" crumb="Privacy Policy" path="/privacy" description="Informativa privacy di EMME SERVICE CAR.">
      <p>La presente informativa descrive come {company.name} tratta i dati personali di visitatori e clienti, ai sensi del Regolamento (UE) 2016/679 (GDPR).</p>
      <H2>Titolare del trattamento</H2>
      <p>{company.name} — {company.address} — P.IVA {company.vat} — {company.email}.</p>
      <H2>Dati raccolti</H2>
      <p>Dati di contatto forniti volontariamente (nome, telefono, email), dettagli della richiesta di trasporto e, solo previo consenso, la posizione geografica che condividi per ricevere assistenza.</p>
      <H2>Finalità e base giuridica</H2>
      <p>I dati sono utilizzati esclusivamente per rispondere alla richiesta e organizzare il servizio. La posizione non viene mai salvata senza il tuo consenso.</p>
      <H2>Conservazione</H2>
      <p>Conserviamo i dati solo per il tempo necessario a gestire la richiesta e gli eventuali obblighi correlati.</p>
      <H2>Diritti dell'interessato</H2>
      <p>Puoi richiedere accesso, rettifica, cancellazione o limitazione dei tuoi dati scrivendo a {company.email}.</p>
    </LegalShell>
  );
}

export function CookiePolicy() {
  const { lang } = useLang();
  if (lang === "en") {
    return (
      <LegalShell title="Cookie Policy" crumb="Cookie Policy" path="/cookie" description="Cookie policy of EMME SERVICE CAR.">
        <p>This website uses only technical cookies necessary for its operation. No marketing or profiling tracking is active without your explicit consent.</p>
        <H2>Technical cookies</H2>
        <p>Used to store your language preference and cookie choice. They do not require consent.</p>
        <H2>Analytics / marketing</H2>
        <p>Any analytics or marketing tools (e.g. Google Analytics, Meta Pixel) would only be loaded after your consent via the cookie banner.</p>
        <H2>Managing cookies</H2>
        <p>You can change your choice at any time from your browser settings.</p>
      </LegalShell>
    );
  }
  return (
    <LegalShell title="Cookie Policy" crumb="Cookie Policy" path="/cookie" description="Cookie policy di EMME SERVICE CAR.">
      <p>Questo sito utilizza esclusivamente cookie tecnici necessari al suo funzionamento. Nessun tracciamento marketing o di profilazione è attivo senza il tuo consenso esplicito.</p>
      <H2>Cookie tecnici</H2>
      <p>Utilizzati per memorizzare la preferenza di lingua e la scelta sui cookie. Non richiedono consenso.</p>
      <H2>Analytics / marketing</H2>
      <p>Eventuali strumenti di analisi o marketing (es. Google Analytics, Meta Pixel) verrebbero caricati solo dopo il consenso tramite il banner cookie.</p>
      <H2>Gestione dei cookie</H2>
      <p>Puoi modificare la tua scelta in qualsiasi momento dalle impostazioni del browser.</p>
    </LegalShell>
  );
}
